/**
 * HomeGlobeHero.jsx — Jun 2026 · Globe-led homepage hero.
 *
 * Reuses the EXISTING globe stack (MiniGlobe → react-globe.gl, already a
 * dependency) rather than adding a new library. Country data and
 * learned-state come from the same sources as the /globe route, so no
 * metadata is duplicated.
 *
 * Performance is guarded up front — the heavy WebGL bundle is only
 * lazy-loaded and mounted when ALL of these pass:
 *   • WebGL is available (hasWebGL)
 *   • not Save-Data
 *   • deviceMemory (when known) >= 4 GB
 *   • hardwareConcurrency (when known) >= 4
 *   • prefers-reduced-motion is NOT set
 *   • the hero is near the viewport (IntersectionObserver)
 * Otherwise a lightweight static fallback renders — a real, accessible
 * "Explore the globe" button into the full /globe route. Never blocks
 * child access and never needs WebGL.
 */
import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe2, Shuffle, ArrowRight } from "lucide-react";
import { COUNTRIES } from "@/data/countries";
import { loadProgress } from "@/lib/progress";
import { COUNTRY_COUNT } from "@/lib/siteFacts";
import { hasWebGL } from "@/lib/webgl";
import WebGLBoundary from "@/components/WebGLBoundary";
import {
  trackGlobeMounted, trackGlobeCountryTapped, trackGlobeSpinRandom, trackHomeSectionViewed,
} from "@/lib/analytics";

const MiniGlobe = lazy(() => import("@/components/MiniGlobe"));
import GlobeSearch from "@/components/GlobeSearch";
import PlaceCard from "@/components/PlaceCard";

function _reducedMotion() {
  try { return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}
function _saveData() {
  try { return navigator.connection?.saveData === true; } catch { return false; }
}
/** Perf gate — returns true only when a 3D globe is safe to mount. */
function globeAllowed() {
  if (typeof window === "undefined") return false;
  if (!hasWebGL()) return false;
  if (_saveData()) return false;
  try {
    const mem = navigator.deviceMemory;      // may be undefined
    if (typeof mem === "number" && mem < 2) return false;
  } catch { /* ignore — treat unknown as OK */ }
  return true;
}
/** Auto-rotate only when motion is welcome and the device has headroom. */
function globeAutoRotate() {
  if (_reducedMotion()) return false;
  try {
    const mem = navigator.deviceMemory, cores = navigator.hardwareConcurrency;
    if ((typeof mem === "number" && mem < 4) || (typeof cores === "number" && cores > 0 && cores < 4)) return false;
  } catch { /* ignore */ }
  return true;
}

function StaticFallback({ onOpen, learnedCount }) {
  return (
    <div
      data-testid="home-globe-fallback"
      className="relative overflow-hidden rounded-3xl border-4 border-slate-900 bg-gradient-to-br from-sky-100 via-white to-emerald-100 p-5 md:p-6 shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
    >
      <div className="flex items-center gap-4">
        <span className="text-6xl md:text-7xl leading-none" role="img" aria-label="Globe">🌍</span>
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">Explore the world</p>
          <h2 className="font-heading text-xl md:text-2xl font-black text-slate-900 leading-tight">
            Spin the globe, discover a country
          </h2>
          <p className="mt-1 text-xs md:text-sm font-bold text-slate-600">
            {COUNTRY_COUNT} countries to explore · {learnedCount} learned so far
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onOpen}
        data-testid="home-globe-open"
        className="mt-4 inline-flex items-center gap-2 h-12 px-5 rounded-full border-[3px] border-slate-900 bg-emerald-300 font-heading font-black text-base shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
      >
        <Globe2 size={16} strokeWidth={3} /> Explore the globe <ArrowRight size={14} strokeWidth={3} />
      </button>
    </div>
  );
}

export default function HomeGlobeHero({ onCountry }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  const [place, setPlace] = useState(null);
  const allowed = useMemo(() => globeAllowed(), []);
  const learnedCount = useMemo(() => {
    try { return loadProgress().countriesLearned.length; } catch { return 0; }
  }, []);

  // Section-view analytics (once per session).
  useEffect(() => { trackHomeSectionViewed("globe"); }, []);

  // Mount the heavy globe only when near the viewport.
  useEffect(() => {
    if (!allowed || !ref.current || near) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [allowed, near]);

  useEffect(() => { if (near) trackGlobeMounted("3d"); }, [near]);

  const openFullGlobe = () => navigate("/globe");

  const handlePick = (country) => {
    if (!country) return;
    if (country.place) { setPlace(country.place); return; } // tapped a searched city pin → show its card, stay here
    trackGlobeCountryTapped(country.code || "");
    if (typeof onCountry === "function") onCountry(country);
    else navigate("/globe");
  };

  const spinRandom = () => {
    trackGlobeSpinRandom();
    const c = COUNTRIES[Math.floor(Math.random() * COUNTRIES.length)];
    handlePick(c);
  };

  if (!allowed) {
    return (
      <section ref={ref} aria-label="Explore the globe" data-testid="home-globe-hero" className="mt-2">
        <StaticFallback onOpen={openFullGlobe} learnedCount={learnedCount} />
      </section>
    );
  }

  return (
    <section ref={ref} aria-label="Explore the globe" data-testid="home-globe-hero" className="mt-2">
      <GlobeSearch className="mb-3" globeTestId="home-globe" onPlace={(p) => { setNear(true); setPlace(p); }} />
      <div className="relative overflow-hidden rounded-3xl border-4 border-slate-900 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 shadow-[6px_6px_0_0_rgba(15,23,42,1)]">
        <div className="relative mx-auto h-[280px] md:h-[420px] w-full flex items-center justify-center">
          {near ? (
            <WebGLBoundary fallback={<StaticFallback onOpen={openFullGlobe} learnedCount={learnedCount} />}>
              <Suspense fallback={<div className="text-white/70 text-sm font-bold" data-testid="home-globe-loading">Loading globe…</div>}>
                <MiniGlobe
                  onPick={handlePick}
                  autoRotate={globeAutoRotate()}
                  showLearned
                  enableZoom={false}
                  allCountryDots
                  cameraAltitude={2.0}
                  testId="home-globe"
                />
              </Suspense>
            </WebGLBoundary>
          ) : (
            <div className="text-white/70 text-sm font-bold">Preparing globe…</div>
          )}
        </div>

        {/* Overlay — live counts + actions. Positioned so it never blocks
            the globe's drag area (top/bottom bars only). */}
        <div className="pointer-events-none absolute inset-x-0 top-0 p-3 flex items-start justify-between gap-2">
          <span className="pointer-events-none inline-flex items-center gap-1.5 rounded-full border-2 border-white/30 bg-black/30 px-3 py-1 text-[11px] font-black text-white backdrop-blur">
            <Globe2 size={12} strokeWidth={3} /> {COUNTRY_COUNT} countries
          </span>
          <span className="pointer-events-none inline-flex items-center gap-1.5 rounded-full border-2 border-white/30 bg-black/30 px-3 py-1 text-[11px] font-black text-amber-200 backdrop-blur" data-testid="home-globe-learned">
            ⭐ {learnedCount} learned
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={spinRandom}
            data-testid="home-globe-random"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-xs md:text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
          >
            <Shuffle size={14} strokeWidth={3} /> Surprise me
          </button>
          <button
            type="button"
            onClick={openFullGlobe}
            data-testid="home-globe-open"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-[3px] border-slate-900 bg-emerald-300 font-bold text-xs md:text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
          >
            Explore fully <ArrowRight size={14} strokeWidth={3} />
          </button>
        </div>
      </div>
      {place && <PlaceCard place={place} onClose={() => setPlace(null)} onOpenCountry={(c) => { setPlace(null); handlePick(c); }} />}
    </section>
  );
}
